import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Loader2, MapPin, Clock, Camera, LogIn, LogOut, CheckCircle2, Upload, X, Bell, BellRing, Navigation, Briefcase, StickyNote, CalendarDays } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter, AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel } from "@/components/ui/alert-dialog";
import { StatusBadge } from "@/components/Shell";
import { PhotoLightbox } from "@/components/PhotoLightbox";
import { ClockCard } from "@/components/worker/ClockCard";
import { api, photoSrc, captureGps, compressImageFile } from "@/lib/api";
import { enablePushAlerts, checkPushEnabled, pushSupported } from "@/lib/push";
import { fmtDuration, fmtDate, jobTypeLabel, jobKind } from "@/lib/format";

export const MyJobsTab = ({ focusJobId, onFocusHandled }) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [alertStatus, setAlertStatus] = useState("");
  const [bannerDismissed, setBannerDismissed] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setJobs(await api.jobs()); } catch { toast.error("Failed to load jobs"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { checkPushEnabled().then((on) => { if (on) setAlertStatus("enabled"); }); }, []);

  // Deep-link: open a specific job when arriving from a notification (?job=<id>).
  useEffect(() => {
    if (focusJobId && jobs.length) {
      const j = jobs.find((x) => x.id === focusJobId);
      if (j) { setSelected(j); onFocusHandled && onFocusHandled(); }
    }
  }, [focusJobId, jobs]);

  const enableAlerts = async () => {
    // Already on? Fire a test push so the worker can confirm it's working.
    if (alertStatus === "enabled") {
      try {
        const r = await api.pushTest();
        if (r.sent > 0) toast.success("Test notification sent — check your notifications");
        else if (r.subs === 0) { setAlertStatus(""); toast.info("Alerts aren't active on this device — tap again to re-enable"); }
        else toast.error("Couldn't deliver a test — re-enable alerts and try again");
      } catch { toast.error("Couldn't send a test notification"); }
      return;
    }
    const r = await enablePushAlerts();
    setAlertStatus(r);
    if (r === "enabled") {
      toast.success("Job alerts enabled on this device");
      try { await api.pushTest(); } catch { /* best-effort confirmation push */ }
    }
    else if (r === "no-sw") toast.info("Add Cross Jobs to your home screen first, then enable alerts");
    else if (r === "denied") toast.error("Notifications are blocked — allow them in your phone settings");
    else if (r === "unsupported") toast.error("This device doesn't support push notifications");
    else toast.error("Couldn't enable alerts — try again");
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 animate-spin text-primary" /></div>;

  const active = jobs.filter((j) => j.status !== "completed");

  return (
    <div className="fade-up">
      {pushSupported() && alertStatus !== "enabled" && !bannerDismissed && (
        <div data-testid="enable-alerts-banner" className="mb-5 flex items-center gap-3 rounded-2xl border border-primary/30 bg-primary/5 p-4">
          <div className="w-10 h-10 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
            <BellRing className="w-5 h-5 text-primary" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-sm">Turn on notifications</p>
            <p className="text-xs text-muted-foreground">Get instant alerts for new jobs and roster changes — even when the app is closed.</p>
          </div>
          <Button data-testid="banner-enable-alerts-btn" size="sm" onClick={enableAlerts} className="rounded-full bg-primary hover:bg-primary/90 text-white shrink-0">Turn on</Button>
          <button data-testid="banner-dismiss-btn" aria-label="Dismiss" onClick={() => setBannerDismissed(true)} className="text-muted-foreground hover:text-foreground shrink-0"><X className="w-4 h-4" /></button>
        </div>
      )}
      <ClockCard />
      <div className="flex items-start justify-between gap-3 mb-6">
        <div>
          <h2 className="font-display text-2xl font-bold mb-1">My Jobs</h2>
          <p className="text-sm text-muted-foreground">{active.length} active job{active.length !== 1 ? "s" : ""}</p>
        </div>
        <Button data-testid="enable-alerts-btn" variant="outline" size="sm" className="rounded-full shrink-0" onClick={enableAlerts}>
          {alertStatus === "enabled"
            ? <><BellRing className="w-4 h-4 mr-1.5 text-primary" /> Alerts on · test</>
            : <><Bell className="w-4 h-4 mr-1.5" /> Enable alerts</>}
        </Button>
      </div>
      {active.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-border rounded-2xl"><p className="text-muted-foreground">No active jobs right now. Completed jobs move to the Completed tab.</p></div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {active.map((j) => (
            <div key={j.id} data-testid={`myjob-card-${j.id}`} onClick={() => setSelected(j)}
              role="button" tabIndex={0}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSelected(j); } }}
              className="cursor-pointer text-left bg-card border border-border rounded-xl p-5 hover:border-primary/50 hover:shadow-lg transition-all">
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-semibold leading-tight">{j.name}</h3>
                <StatusBadge status={j.status} />
              </div>
              <p className="text-xs text-muted-foreground mt-1">{jobTypeLabel(j)}</p>
              {j.scheduled_date && (
                <p data-testid={`myjob-scheduled-${j.id}`} className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-secondary px-2 py-0.5 text-xs font-medium text-muted-foreground">
                  <CalendarDays className="w-3.5 h-3.5 shrink-0" /> {fmtDate(j.scheduled_date)}
                </p>
              )}
              {j.client && (
                <p className="text-sm text-muted-foreground mt-3 flex items-center gap-1.5" data-testid={`myjob-client-${j.id}`}>
                  <Briefcase className="w-3.5 h-3.5 shrink-0" /> {j.client}
                </p>
              )}
              {j.address && (
                <p className="text-sm text-muted-foreground mt-1.5 flex items-start gap-1.5" data-testid={`myjob-address-${j.id}`}>
                  <MapPin className="w-3.5 h-3.5 shrink-0 mt-0.5" /> <span className="line-clamp-2">{j.address}</span>
                </p>
              )}
              {j.description && (
                <p className="text-sm text-foreground/80 mt-2 line-clamp-3" data-testid={`myjob-desc-${j.id}`}>{j.description}</p>
              )}
              {j.worker_notes && (
                <p className="text-xs text-muted-foreground mt-1.5 flex items-start gap-1.5" data-testid={`myjob-notes-${j.id}`}>
                  <StickyNote className="w-3.5 h-3.5 shrink-0 mt-0.5" /> <span className="line-clamp-3">{j.worker_notes}</span>
                </p>
              )}
              <div className="flex items-center gap-3 mt-4 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {fmtDuration(j.total_seconds)}</span>
                <span className="inline-flex items-center gap-1"><Camera className="w-3.5 h-3.5" /> {j.photo_count}</span>
                {j.open_entry_id && <span className="ml-auto inline-flex items-center gap-1 text-emerald-600 font-medium"><span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" /> On site</span>}
              </div>
              {j.address && (
                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(j.address)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`card-navigate-${j.id}`}
                  onClick={(e) => e.stopPropagation()}
                  className="mt-4 w-full inline-flex items-center justify-center gap-1.5 rounded-full bg-primary/10 text-primary text-sm font-semibold px-3 py-2 hover:bg-primary/20 transition-colors"
                >
                  <Navigation className="w-4 h-4" /> Navigate
                </a>
              )}
            </div>
          ))}
        </div>
      )}
      {selected && <WorkerJobDialog job={selected} onClose={() => setSelected(null)} onChanged={load} />}
    </div>
  );
};

// Which photo kinds a worker can add, per job type.
// Delivery: drop-off + issue only. Site clean (clean up): before/after/issue.
// Standard: completion + before/after/issue.
const KINDS_BY_TYPE = {
  delivery: [
    { v: "delivery", label: "Drop-off" },
    { v: "issue", label: "Issue" },
  ],
  site_clean: [
    { v: "before", label: "Before" },
    { v: "after", label: "After" },
    { v: "issue", label: "Issue" },
  ],
  standard: [
    { v: "completion", label: "Completion" },
    { v: "before", label: "Before" },
    { v: "after", label: "After" },
    { v: "issue", label: "Issue" },
  ],
};
const kindsFor = (t) => KINDS_BY_TYPE[t] || KINDS_BY_TYPE.standard;

export const WorkerJobDialog = ({ job, onClose, onChanged }) => {
  const [d, setD] = useState(job);
  const [photos, setPhotos] = useState([]);
  const [notes, setNotes] = useState(job.worker_notes || "");
  const [removed, setRemoved] = useState(job.remarks || "");
  const [kind, setKind] = useState(kindsFor(jobKind(job))[0].v);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null); // { done, total } while uploading
  const [lightbox, setLightbox] = useState(null);
  const [confirmComplete, setConfirmComplete] = useState(false);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  const kinds = kindsFor(jobKind(d));

  const refresh = async () => {
    const [fresh, ph] = await Promise.all([api.job(job.id), api.jobPhotos(job.id)]);
    setD(fresh); setPhotos(ph); setNotes(fresh.worker_notes || ""); setRemoved(fresh.remarks || "");
  };
  useEffect(() => { refresh().catch(() => {}); }, [job.id]);
  // Keep the selected kind valid for this job's type.
  useEffect(() => {
    setKind((k) => (kinds.some((x) => x.v === k) ? k : kinds[0].v));
  }, [kinds]);

  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); if (msg) toast.success(msg); await refresh(); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    finally { setBusy(false); }
  };

  const onFile = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setBusy(true);
    setProgress({ done: 0, total: files.length });
    let ok = 0;
    for (let i = 0; i < files.length; i++) {
      try {
        const image_base64 = await compressImageFile(files[i]);
        await api.addPhoto(job.id, { kind, image_base64, note: "" });
        ok++;
      } catch { /* keep going with the rest */ }
      setProgress({ done: i + 1, total: files.length });
    }
    setProgress(null);
    setBusy(false);
    try { await refresh(); onChanged(); } catch { /* noop */ }
    if (ok === 0) toast.error("Upload failed — please try again");
    else if (ok === files.length) toast.success(files.length > 1 ? `${ok} photos uploaded` : "Photo uploaded");
    else toast.warning(`${ok} of ${files.length} photos uploaded — the rest failed, please retry`);
  };

  const onSite = !!d.open_entry_id;

  const doComplete = () => run(async () => { await api.updateJob(job.id, { status: "completed" }); api.onJobComplete(job.id).catch(() => {}); }, "Job marked complete");
  const requiresCompletionPhoto = jobKind(d) === "standard";
  const hasCompletionPhoto = photos.some((p) => p.kind === "completion");
  const onCompleteClick = () => { if (!requiresCompletionPhoto || hasCompletionPhoto) doComplete(); else setConfirmComplete(true); };

  const saveNote = async (photoId, value) => {
    const note = (value || "").trim();
    const current = photos.find((p) => p.id === photoId);
    if ((current?.note || "") === note) return;
    try {
      await api.setPhotoNote(photoId, note);
      setPhotos((ps) => ps.map((p) => (p.id === photoId ? { ...p, note } : p)));
    } catch { toast.error("Could not save note"); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between gap-3 pr-6">
            <DialogTitle className="font-display text-xl">{d.name}</DialogTitle>
            <StatusBadge status={d.status} />
          </div>
        </DialogHeader>

        <div className="space-y-5 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <div><p className="text-xs text-muted-foreground">Type</p><p className="font-medium">{jobTypeLabel(d)}</p></div>
            <div><p className="text-xs text-muted-foreground">Client</p><p className="font-medium">{d.client || "—"}</p></div>
            <div className="col-span-2">
              <p className="text-xs text-muted-foreground">Address</p>
              {d.address ? (
                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(d.address)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="navigate-address-link"
                  className="font-medium text-primary inline-flex items-start gap-1.5 hover:underline"
                >
                  <Navigation className="w-4 h-4 mt-0.5 shrink-0" /> {d.address}
                </a>
              ) : (
                <p className="font-medium">—</p>
              )}
            </div>
          </div>
          {d.description && <div><p className="text-xs text-muted-foreground mb-1">Description</p><p>{d.description}</p></div>}

          {/* Clock on/off for this job */}
          <div className="rounded-xl border border-border p-4 flex items-center justify-between bg-secondary/40">
            <div>
              <p className="font-semibold">{onSite ? "You're checked in" : "Site time"}</p>
              <p className="text-xs text-muted-foreground">Total logged: <span className="font-mono">{fmtDuration(d.total_seconds)}</span></p>
            </div>
            {onSite ? (
              <Button data-testid="job-checkout-btn" disabled={busy} onClick={() => run(async () => { await api.checkOut(job.id); const g = await captureGps(); api.gpsLog(job.id, { type: "out", ...g }).catch(() => {}); }, "Checked out")} className="rounded-full bg-stone-700 hover:bg-stone-800 text-white">
                <LogOut className="w-4 h-4 mr-2" /> Check out
              </Button>
            ) : (
              <Button data-testid="job-checkin-btn" disabled={busy || d.status === "completed"} onClick={() => run(async () => { await api.checkIn(job.id); const g = await captureGps(); api.gpsLog(job.id, { type: "in", ...g }).catch(() => {}); }, "Checked in")} className="rounded-full bg-primary hover:bg-primary/90 text-white h-12 px-7 text-base font-semibold shadow-lg shadow-primary/30 ring-2 ring-primary/20">
                <LogIn className="w-5 h-5 mr-2" /> Check in on-site
              </Button>
            )}
          </div>

          {/* Photos */}
          <div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
              <p className="text-xs text-muted-foreground">Photos ({photos.length})</p>
              <div className="flex items-center gap-2 flex-wrap">
                <Select value={kind} onValueChange={setKind}>
                  <SelectTrigger data-testid="photo-kind-select" className="h-8 w-36 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{kinds.map((k) => <SelectItem key={k.v} value={k.v}>{k.label}</SelectItem>)}</SelectContent>
                </Select>
                <input ref={cameraRef} type="file" accept="image/*" capture="environment" hidden onChange={onFile} data-testid="photo-camera-input" />
                <Button size="sm" disabled={busy} onClick={() => cameraRef.current?.click()} data-testid="take-photo-btn" className="rounded-full h-8 bg-primary hover:bg-primary/90 text-white">
                  {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Camera className="w-3.5 h-3.5 mr-1.5" />} {busy ? "Uploading…" : "Take photo"}
                </Button>
                <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={onFile} data-testid="photo-file-input" />
                <Button size="sm" variant="outline" disabled={busy} onClick={() => fileRef.current?.click()} data-testid="upload-photo-btn" className="rounded-full h-8">
                  {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />} {busy ? "Uploading…" : "Upload"}
                </Button>
              </div>
            </div>
            {progress && (
              <div className="mb-3" data-testid="upload-progress">
                <div className="flex justify-between text-[11px] text-muted-foreground mb-1">
                  <span>Uploading photos…</span><span>{progress.done}/{progress.total}</span>
                </div>
                <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
                  <div className="h-full bg-primary transition-all duration-300" style={{ width: `${Math.round((progress.done / progress.total) * 100)}%` }} />
                </div>
              </div>
            )}
            {photos.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {photos.map((p, i) => (
                  <div key={p.id} className="relative group">
                    <img src={photoSrc(p.image_base64)} alt={p.kind} onClick={() => setLightbox(i)} data-testid={`worker-photo-${p.id}`} className="rounded-lg aspect-square object-cover border border-border w-full cursor-zoom-in" />
                    <span className="absolute bottom-1 left-1 text-[9px] px-1.5 py-0.5 rounded bg-black/60 text-white capitalize">{p.kind}</span>
                    <button data-testid={`delete-photo-${p.id}`} onClick={() => run(() => api.deletePhoto(p.id), "Photo removed")} className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 bg-black/60 text-white rounded-full p-0.5"><X className="w-3 h-3" /></button>
                    <input
                      data-testid={`photo-note-${p.id}`}
                      defaultValue={p.note || ""}
                      placeholder="Add a note…"
                      maxLength={300}
                      onBlur={(e) => saveNote(p.id, e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                      className="mt-1 w-full text-[11px] px-2 py-1 rounded-md border border-border bg-background focus:border-primary focus:outline-none"
                    />
                  </div>
                ))}
              </div>
            ) : <p className="text-xs text-muted-foreground">No photos yet.</p>}
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label className="text-xs">Worker notes</Label>
            <Textarea data-testid="worker-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Add remarks about this job…" />
            <Button size="sm" variant="outline" disabled={busy} data-testid="save-notes-btn" onClick={() => run(() => api.updateJob(job.id, { worker_notes: notes }), "Notes saved")} className="rounded-full">Save notes</Button>
          </div>

          {/* Items removed (Site Clean) */}
          {jobKind(d) === "site_clean" && (
            <div className="space-y-2" data-testid="worker-removed-items-section">
              <Label className="text-xs">Items removed</Label>
              <Textarea data-testid="worker-removed-items-textarea" value={removed} onChange={(e) => setRemoved(e.target.value)} placeholder="List what was removed from the site…" className="min-h-[110px]" />
              <Button size="sm" variant="outline" disabled={busy} data-testid="worker-save-removed-items" onClick={() => run(() => api.updateJob(job.id, { remarks: removed }), "Removed-items note saved")} className="rounded-full">Save items</Button>
            </div>
          )}

          {/* Complete */}
          <div className="border-t border-border pt-4">
            {d.status !== "completed" ? (
              <Button data-testid="worker-complete-btn" disabled={busy} onClick={onCompleteClick} className="w-full rounded-full bg-emerald-600 hover:bg-emerald-700 text-white h-11">
                <CheckCircle2 className="w-4 h-4 mr-2" /> Mark job complete
              </Button>
            ) : (
              <p className="text-center text-sm text-emerald-600 font-medium flex items-center justify-center gap-2"><CheckCircle2 className="w-4 h-4" /> Completed</p>
            )}
          </div>
        </div>
      </DialogContent>
      <AlertDialog open={confirmComplete} onOpenChange={setConfirmComplete}>
        <AlertDialogContent data-testid="completion-photo-nudge">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><Camera className="w-5 h-5 text-primary" /> No completion photo yet</AlertDialogTitle>
            <AlertDialogDescription>
              You haven&apos;t added a <span className="font-medium">Completion</span> photo for this job. Completion photos are your proof of work for the client. Add one now, or complete the job anyway?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="nudge-add-photo-btn" onClick={() => { setKind("completion"); }}>Add photo</AlertDialogCancel>
            <AlertDialogAction data-testid="nudge-complete-anyway-btn" onClick={() => { setConfirmComplete(false); doComplete(); }} className="bg-emerald-600 hover:bg-emerald-700 text-white">Complete anyway</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <PhotoLightbox photos={photos} index={lightbox} onClose={() => setLightbox(null)} onIndexChange={setLightbox} />
    </Dialog>
  );
};
